// Automatic update of the single page app: in-store devices keep the same tab open for days, so
// the app reloads itself when a new version is deployed. See docs/app-auto-update.md

// version of the running app. The build writes the same value in /version.json
export const runningAppVersion: string = __APP_VERSION__;

export const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;
export const RELOAD_CHECK_INTERVAL_MS = 15 * 1000;
// without interaction during this delay, a reload does not disturb anybody
export const IDLE_BEFORE_RELOAD_MS = 2 * 60 * 1000;

export type ReloadTrigger = "navigation" | "timer";

export function parseVersionFile(content: unknown): string | null {
  if (typeof content !== "object" || content === null) {
    return null;
  }
  const version = (content as { version?: unknown }).version;
  return typeof version === "string" && version !== "" ? version : null;
}

// A reload must never loose data nor interrupt a client:
// - not while a save is pending or when a page blocks updates
// - only when the page changes, when the app is hidden or when nobody touched the device recently
export function shouldReloadNow({
  runningVersion,
  deployedVersion,
  alreadyReloadedForVersion,
  hasPendingSaves,
  updateBlocked,
  trigger,
  idleMs,
  pageHidden,
}: {
  runningVersion: string;
  deployedVersion: string | null;
  // protection against reload loops (e.g. index.html still cached while version.json is new)
  alreadyReloadedForVersion: string | null;
  hasPendingSaves: boolean;
  updateBlocked: boolean;
  trigger: ReloadTrigger;
  idleMs: number;
  pageHidden: boolean;
}): boolean {
  if (deployedVersion === null || deployedVersion === runningVersion) {
    return false;
  }
  if (alreadyReloadedForVersion === deployedVersion) {
    return false;
  }
  if (hasPendingSaves || updateBlocked) {
    return false;
  }
  return trigger === "navigation" || pageHidden || idleMs >= IDLE_BEFORE_RELOAD_MS;
}

// --- saves in progress: a reload would loose them

const pendingSaves = new Map<string, number>();
let pendingSaveSequence = 0;

// returns a token to give to endPendingSave
export function beginPendingSave(key: string): number {
  pendingSaveSequence += 1;
  pendingSaves.set(key, pendingSaveSequence);
  return pendingSaveSequence;
}

// A newer save of the same key may have started: only the latest token ends the pending state.
// Without token, the pending state is always ended.
export function endPendingSave(key: string, token?: number) {
  if (token === undefined || pendingSaves.get(key) === token) {
    pendingSaves.delete(key);
  }
}

export function hasPendingSaves(): boolean {
  return pendingSaves.size > 0;
}

// --- pages can block updates (see useBlockAppUpdate)

let updateBlockers = 0;

export function blockAppUpdate(): () => void {
  updateBlockers += 1;
  let released = false;
  return () => {
    if (!released) {
      released = true;
      updateBlockers -= 1;
    }
  };
}

export function isAppUpdateBlocked(): boolean {
  return updateBlockers > 0;
}
